import axios from 'axios';
import {Form} from '@/plugins/FWForms/index';
import * as types from '../mutation-types';
import {push} from 'notivue';

export interface HqRentalsState {
    loading: Record<string, boolean>;
    form: Form<any>;
    connections: any[];
    listings: any[];
    bookings: any[];
    contacts: any[];
}

const state: HqRentalsState = {
    loading: {
        login: false,
        test: false,
        listings: false,
        bookings: false,
        contacts: false,
        meta: false,
        settings: false,
    },
    form: new Form(),
    connections: [],
    listings: [],
    bookings: [],
    contacts: [],
};

const getters = {
    form: state => state.form,
    connections: state => state.connections,
    loading: state => state.loading,
    listings: state => state.listings,
    bookings: state => state.bookings,
    contacts: state => state.contacts,
};

const mutations = {
    [types.SET_HQRENTALS_LOADING](state, loading) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_HQRENTALS_CONNECTIONS](state, connections) {
        state.connections = connections;
    },
    [types.SET_HQRENTALS_LISTINGS](state, listings) {
        state.listings = listings;
    },
    [types.SET_HQRENTALS_BOOKINGS](state, bookings) {
        state.bookings = bookings;
    },
    [types.SET_HQRENTALS_CONTACTS](state, contacts) {
        state.contacts = contacts;
    },
};

const actions = {

    async login({commit, rootGetters}, credentials): Promise<{ provider: any } | undefined> {
        commit('SET_HQRENTALS_LOADING', {subject: 'login', loading: true});

        try {
            const response = await axios.post(`/v1/company/${rootGetters['company/company'].uuid}/connections/hq-rentals`, credentials);
            push.success({message: 'HQ Rentals logged in successfully!'});

            return {provider: response?.data?.provider};
        } catch {
            return undefined;
        } finally {
            commit('SET_HQRENTALS_LOADING', {subject: 'login', loading: false});
        }
    },

    async connect({commit, rootGetters}, data): Promise<{ providers: any[] } | undefined> {
        commit('SET_HQRENTALS_LOADING', {subject: 'login', loading: true});

        try {
            const response = await axios.post(`/v1/company/${rootGetters['company/company'].uuid}/connections/hq-rentals/connect`, data);
            push.success({message: 'HQ Rentals connection connected successfully!'});

            return {providers: response?.data?.providers ?? []};
        } catch {
            return undefined;
        } finally {
            commit('SET_HQRENTALS_LOADING', {subject: 'login', loading: false});
        }
    },

    async setMeta({commit, rootGetters}, provider): Promise<{ fields: any } | undefined> {
        commit('SET_HQRENTALS_LOADING', {subject: 'meta', loading: true});

        try {
            const response = await axios.post(`/v1/company/${rootGetters['company/company'].uuid}/connections/hq-rentals/${provider.uuid}/set-meta`);

            return {fields: response?.data?.fields};
        } catch {
            return undefined;
        } finally {
            commit('SET_HQRENTALS_LOADING', {subject: 'meta', loading: false});
        }
    },

    saveSettings({commit, rootGetters}, data) {
        commit('SET_HQRENTALS_LOADING', {subject: 'settings', loading: true});

        axios.put(`/v1/company/${rootGetters['company/company'].uuid}/connections/hq-rentals/${data.provider_uuid}/settings`, data)
            .then(() => {
                push.success({message: 'HQ Rentals settings saved successfully!'});
            })
            .catch(() => {
            })
            .finally(() => {
                commit('SET_HQRENTALS_LOADING', {subject: 'settings', loading: false});
            });
    },

    async testConnection({commit}, provider): Promise<boolean> {
        commit('SET_HQRENTALS_LOADING', {subject: 'test', loading: true});

        try {
            await axios.post(`/v1/connections/hq-rentals/${provider.uuid}/test-connection`);
            push.success({message: 'HQ Rentals connection connected successfully!'});

            return true;
        } catch {
            return false;
        } finally {
            commit('SET_HQRENTALS_LOADING', {subject: 'test', loading: false});
        }
    },

    fetchListings({commit, rootGetters}, provider_uuid) {
        commit('SET_HQRENTALS_LOADING', {subject: 'listings', loading: true});

        axios.get(`/v1/company/${rootGetters['company/company'].uuid}/connections/hq-rentals/${provider_uuid}/listings`)
            .then((response) => {
                commit('SET_HQRENTALS_LISTINGS', response?.data?.listings);
                push.success({message: 'HQ Rentals connection connected successfully!'});
            }).catch(() => {
        }).finally(() => {
            commit('SET_HQRENTALS_LOADING', {subject: 'listings', loading: false});
        });
    },

    fetchBookings({commit, rootGetters}, provider_uuid) {
        commit('SET_HQRENTALS_LOADING', {subject: 'bookings', loading: true});

        axios.get(`/v1/company/${rootGetters['company/company'].uuid}/connections/hq-rentals/${provider_uuid}/bookings`)
            .then((response) => {
                commit('SET_HQRENTALS_BOOKINGS', response?.data?.bookings);
            }).catch(() => {
        }).finally(() => {
            commit('SET_HQRENTALS_LOADING', {subject: 'bookings', loading: false});
        });
    },

    fetchContacts({commit, rootGetters}, provider_uuid) {
        commit('SET_HQRENTALS_LOADING', {subject: 'contacts', loading: true});

        axios.get(`/v1/company/${rootGetters['company/company'].uuid}/connections/hq-rentals/${provider_uuid}/contacts`)
            .then((response) => {
                commit('SET_HQRENTALS_CONTACTS', response?.data?.contacts);
            }).catch(() => {
        }).finally(() => {
            commit('SET_HQRENTALS_LOADING', {subject: 'contacts', loading: false});
        });
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
